import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import "react-datepicker/dist/react-datepicker.css";
import Input from '../../../components/common/Input/Input';
import Asterisk from '../../../components/common/Asterisk/Asterisk';
import RadioButton from '../../../components/common/RadioGroup/RadioButton';
import "./ContactInfo.css";
import { useDispatch, useSelector } from 'react-redux';
import { ProfileDetailsState } from '../../../redux/reducers/getProfile/getProfileDetailsReducer';
import { ProfileOverviewState } from '../../../redux/reducers/getProfile/getProfileOverviewReducer';
import { SaveDataState } from '../../../redux/reducers/getSaveData/saveDataReducer';
import { getProfileDetails, putContactInfo, putPieAnalysis, resetGetProfileDetails } from '../../../redux/actions/profile/profileDetails';
import { SaveResultState } from '../../../redux/reducers/getSaveData/saveResultReducer';
import { getFormErrorByName, getFormErrorMessageByName, hasError, useFormErrorContext } from '../../../utils/context/FormErrorContext';
import ErrorMessages from '../../../components/common/ErrorMessages/ErrorMessages';
import AIRPNumberInput from '../../../components/common/AIRPNumberInput/AIRPNumberInput';
import { useReadOnly } from '../../../utils/ReadOnlyContext';
import { toast } from 'react-toastify';
import { selectELSLookups, selectProfileGetDetails, selectProfileGetOverview, selectSaveData, selectSaveResult } from '../../../redux/selectors/exportSelectorPaths';
import { ContactInfoType, initialContactInfo, keysOfContactInfoType } from '../../../types/profileDetails/ContactInfoType';
import { pickFromProfileDetailsType } from '../../../types/profileDetails/ProfileDetailsType';
import Dropdown, { Option } from '../../../components/common/Dropdown/Dropdown';
import { GetElsLookupsState } from '../../../redux/reducers/getLookups/getElsLookupsReducer';
import { initialPieAnalysis, keysOfPieAnalysisType, PieAnalysisType } from '../../../types/profileDetails/PieAnalysisType';
import LoadingSpinner from '../../../components/common/LoadingSpinner/LoadingSpinner';

export default function ContactInfo(): JSX.Element {
    const elsLookupsState: GetElsLookupsState = useSelector(selectELSLookups);
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
    const getProfileDetailsState: ProfileDetailsState = useSelector(selectProfileGetDetails);
    const saveResultState: SaveResultState = useSelector(selectSaveResult);
    const saveData: SaveDataState = useSelector(selectSaveData);
    const { isReadOnly } = useReadOnly();
    const { t } = useTranslation('contact_info');
    const dispatch = useDispatch();
    const profileType = profileOverview.profile.profile_type_code;
    const isInternalUser = (sessionStorage.getItem("isInternalUser") as string) === "true"
    const currentSnapshotId = sessionStorage.getItem("currentSnapshotId") as string
    const { errors, clearErrors, removeError } = useFormErrorContext();
    const propertyCode = sessionStorage.getItem("propertyCode") as string
    const analysisFlagOptions = (elsLookupsState.dropdowns && elsLookupsState.dropdowns["rentAnalysisOptions"] as Option[]) ?? []

    const [contactInfoData, setContactInfoData] = useState<ContactInfoType>({ ...initialContactInfo });
    const [pieAnalysisData, setPieAnalysisData] = useState<PieAnalysisType>({ ...initialPieAnalysis })
    const [isGetReqSent, setIsGetReqSent] = useState(false);

    useEffect(() => {
        dispatch(resetGetProfileDetails());
        dispatch(getProfileDetails(currentSnapshotId));
        setIsGetReqSent(true);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (isGetReqSent) {
            if (!getProfileDetailsState.loading && !getProfileDetailsState.success) {
                toast.error('Failed to load data');
            }
            else {
                setContactInfoData(pickFromProfileDetailsType(getProfileDetailsState.data, keysOfContactInfoType));
                setPieAnalysisData(pickFromProfileDetailsType(getProfileDetailsState.data, keysOfPieAnalysisType))
            }
        }
    }, [isGetReqSent, getProfileDetailsState])

    useEffect(() => {
        if (saveResultState.status === 200) {
            dispatch(getProfileDetails(currentSnapshotId))
        }
    }, [currentSnapshotId, dispatch, saveResultState.status]);

    const maskPhoneNumber = (value: string | null) => {
        if (value !== null) {
            let numberString = value.replace(/\D+/g, '');
            if (numberString.length > 3) {
                numberString = numberString.slice(0, 3) + "-" + numberString.slice(3);
            }
            if (numberString.length > 7) {
                numberString = numberString.slice(0, 7) + "-" + numberString.slice(7);
            }
            return numberString;
        } else {
            return undefined;
        }

    }

    useEffect(() => {
        if (saveData.saving && !isReadOnly) {
            clearErrors();

            dispatch(putContactInfo(currentSnapshotId, {
                "contact_info_data": contactInfoData,
                "spectral_section": profileType.toLowerCase() + "-contact-info",
                "trigger_spectral": true,
                "property_code": propertyCode,
                "is_internal": isInternalUser
            }))

            // const statusCode = sessionStorage.getItem("campaignStatusCode") as string
            if ((profileType === "GEV" || profileType === "AGS") && isInternalUser && (sessionStorage.getItem("campaignStatusCode") === "INTERNAL_IN_PROGRESS")) {
                dispatch(putPieAnalysis(currentSnapshotId, {
                    "pie_analysis_data": pieAnalysisData,
                    "spectral_section": profileType.toLowerCase() + "-campaign-analysis",
                    "trigger_spectral": true,
                    "property_code": propertyCode,
                    "is_internal": isInternalUser
                }))
            }
        }
    }, [clearErrors, contactInfoData, currentSnapshotId, dispatch, isInternalUser, isReadOnly, pieAnalysisData, profileType, propertyCode, saveData.saving])

    const handleInputChange = (e: React.ChangeEvent) => {
        const target = e.target as HTMLInputElement | HTMLSelectElement;
        const targetName = target.name;
        removeError(getFormErrorByName(errors, targetName)?.primaryKey as string)
        if (targetName === "phoneNumber" || targetName === "phoneExtension" || targetName === "secondaryPhoneNumber" || targetName === "secondaryPhoneExtension") {
            setContactInfoData({ ...contactInfoData, [targetName]: target.value.trim() ? target.value.trim().replace(/-/g, '') : null });
        }
        else if (targetName === "agentFlag") {
            setContactInfoData({ ...contactInfoData, [targetName]: (target.value === "true" ? true : (target.value === "false" ? false : null)) });
        } else {
            setContactInfoData({ ...contactInfoData, [targetName]: target.value ? target.value.trim() : null });
        }
    }

    const fillInFormFields = (profileType: string): JSX.Element[] => {
        const fields = [];
        if (["HTL", "LLE", "MTL", "RHO", "GLF", "LTC"].includes(profileType)) {
            fields.push(<div>
                <div>
                    <label className='top-label' htmlFor='contact-trade-name'>
                        {profileType === "LLE" ? t("property") :
                            profileType === "RHO" ? t("retirement") :
                                profileType === "LTC" ? t("ltc") :
                                    profileType === "GLF" ? t("golf") :
                                        profileType === "MTL" ? t("motel") :
                                            propertyCode === "460" ? t("resort") : t("hotel")}
                        {["HTL", "MTL", "LLE", "LTC", "RHO", "GLF"].includes(profileType) && <> <Asterisk /></>}
                    </label>
                </div>
                <div>
                    <Input name='tradeName' id='contact-trade-name' type='text'
                        value={contactInfoData.tradeName}
                        errorMessage={getFormErrorMessageByName(errors, "tradeName")}
                        isError={hasError(errors, "tradeName")}
                        onChange={handleInputChange}
                        hideErrorCross
                        label={profileType === "LLE" ? t("property") :
                            profileType === "RHO" ? t("retirement") :
                                profileType === "LTC" ? t("ltc") :
                                    profileType === "GLF" ? t("golf") :
                                        profileType === "HTL" ? t("hotel") : t("motel")} />
                </div>
            </div>);
        }
        fields.push(<div>
            <div>
                <label className='top-label' htmlFor='contact-name'>{["HTL", "LLE", "RHO", "LTC", "GLF", "MTL"].includes(profileType) ?
                    t("contact") : t("name")} <Asterisk /></label>
            </div>
            <div>
                {<Input name='contactName' id='contact-name' type='text'
                    label={["HTL", "LLE", "RHO", "LTC", "GLF", "MTL"].includes(profileType) ?
                        t("contact") : t("name")} value={contactInfoData.contactName}
                    errorMessage={getFormErrorMessageByName(errors, "contactName")}
                    onChange={handleInputChange}
                    isError={hasError(errors, "contactName")}
                    hideErrorCross={true} noTimer={true} />}
            </div>
        </div>);
        fields.push(<div>
            <div>
                <label className='top-label' htmlFor='contact-position'>{t("position")}</label>
            </div>
            <div>
                <Input name='contactPosition' id='contact-position' type='text'
                    label={t("position")} value={contactInfoData.contactPosition}
                    errorMessage={getFormErrorMessageByName(errors, "contactPosition")}
                    isError={hasError(errors, "contactPosition")}
                    onChange={handleInputChange}
                    hideErrorCross={true}
                />
            </div>
        </div>);
        fields.push(<div>
            <div>
                <label className='top-label' htmlFor='contact-email'>{t("email")} <Asterisk /></label>
            </div>
            <div>
                <Input name='email' id='contact-email' type='text' label={t("email")}
                    value={contactInfoData.email}
                    onChange={handleInputChange}
                    errorMessage={getFormErrorMessageByName(errors, "email")}
                    isError={hasError(errors, "email")}
                    hideErrorCross={true} noTimer={true} />
            </div>
        </div>);
        fields.push(<div>
            <div className='contact-full-phone-container'>
                <div className='contact-phone-container'>
                    <div>
                        <label className='top-label' htmlFor='contact-phone'>{t("telephone")} <Asterisk /></label>
                    </div>
                    <div>
                        <Input name='phoneNumber'
                            id='contact-phone' type='text' className='contact-phone' label={t("telephone")}
                            value={maskPhoneNumber(contactInfoData.phoneNumber)}
                            errorMessage={getFormErrorMessageByName(errors, "phoneNumber")}
                            onChange={handleInputChange}
                            isError={hasError(errors, "phoneNumber")}
                            hideErrorCross={true} noTimer={true} />
                    </div>
                </div>
                <div className='contact-ext-container'>
                    <div>
                        <label className='top-label' htmlFor='contact-ext'>{t("ext")}</label>
                    </div>
                    <div>
                        <AIRPNumberInput
                            errorBorder="inner"
                            commas={false}
                            name='phoneExtension'
                            initialValue={contactInfoData.phoneExtension}
                            className='border-hover contact-ext'
                            negatives={false}
                            decimals={false}
                            onChange={(value: number | null) => { setContactInfoData({ ...contactInfoData, phoneExtension: value === null ? null : String(value) }) }}
                        />
                    </div>
                </div>
            </div>
            <div className='contact-full-phone-container'>
                <div className='contact-phone-container'>
                    <div>
                        <label className='top-label' htmlFor='contact-secondary-phone'>{t("secondaryTelephone")}</label>
                    </div>
                    <div>
                        <Input name='secondaryPhoneNumber'
                            id='contact-secondary-phone' type='text' className='contact-phone' label={t("telephone")}
                            value={maskPhoneNumber(contactInfoData.secondaryPhoneNumber)}
                            errorMessage={getFormErrorMessageByName(errors, "secondaryPhoneNumber")}
                            onChange={handleInputChange}
                            isError={hasError(errors, "secondaryPhoneNumber")}
                            hideErrorCross={true} noTimer={true} />
                    </div>
                </div>
                <div className='contact-ext-container'>
                    <div>
                        <label className='top-label' htmlFor='contact-secondary-ext'>{t("ext")}</label>
                    </div>
                    <div>
                        <AIRPNumberInput
                            errorBorder="inner"
                            commas={false}
                            name='secondaryPhoneExtension'
                            initialValue={contactInfoData.secondaryPhoneExtension}
                            className='border-hover contact-ext'
                            negatives={false}
                            decimals={false}
                            onChange={(value: number | null) => { setContactInfoData({ ...contactInfoData, secondaryPhoneExtension: value === null ? null : String(value) }) }}
                        />
                    </div>
                </div>
            </div>
        </div>);
        return fields;
    }

    return (
        <>
            {(profileType === "GEV" || profileType === "AGS") && isInternalUser && (sessionStorage.getItem("campaignStatusCode") === "INTERNAL_IN_PROGRESS") && analysisFlagOptions.length > 0 &&
                (elsLookupsState.loading ?
                    <LoadingSpinner loading={true} /> :
                    <div className='airp-badge badge-lighter-suburb-yellow analysis-container'>
                        <label htmlFor='analysisFlag'>{t("analysis")}</label>
                        <Dropdown id="analysisFlag" name='analysisFlag'
                            options={analysisFlagOptions}
                            value={pieAnalysisData.analysisFlag}
                            onChange={(e) => {
                                setPieAnalysisData({
                                    "analysisFlag": (e.target as HTMLSelectElement).value,
                                    "analysisReviewedBy": localStorage.getItem("username") ?? "SYSTEM",
                                    "analysisReviewedDate": (new Date()).toISOString()
                                })
                            }}
                        />
                    </div>
                )
            }
            <div className='contact-info-header'>
                <p className='strong'>{t("heading")}</p>
                <ErrorMessages errors={errors} />
                <table className='contact-info-table' role='none'>
                    <tbody>
                        <tr>
                            <td>
                                <fieldset className={'side-by-side contact-owner-agent-container noborder-fieldset'} aria-label='contact-owner-agent-flag-group'>
                                    <legend className='invisible-legend'>Agent Flag group</legend>
                                    <div className='radio-wrapper'>
                                        <span className={`side-by-side ${hasError(errors, "agentFlag") ? "radio-error" : ""}`}>
                                            <RadioButton id='contact-owner' name='agentFlag' text={t("owner")} textPosition='right'
                                                value="false" currentValue={contactInfoData.agentFlag === null ? "" : contactInfoData.agentFlag}
                                                ariaRole='radio'
                                                onChange={handleInputChange}
                                            />
                                            <RadioButton id='contact-agent' name='agentFlag' text={t("agent")} textPosition='right'
                                                value="true" currentValue={contactInfoData.agentFlag === null ? "" : contactInfoData.agentFlag}
                                                ariaRole='radio'
                                                onChange={handleInputChange}
                                            />
                                        </span>
                                        {hasError(errors, "agentFlag") ? <div className="input-error-message radio-error">{getFormErrorMessageByName(errors, "agentFlag")}</div> : ""}
                                    </div>
                                </fieldset>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <div className="contact-info-form-container">
                                    {fillInFormFields(profileType).map((field, index) => <Fragment key={index}>{field}</Fragment>)}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </>
    );
}